import { useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { BsCamera } from "react-icons/bs";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../Context/AppContext";
import { isLoggedIn } from "../../auth/authStorage";
import { useCurrentMembership } from "../../hooks/useCurrentMembership";
import { hasProfessionalPhoto } from "../../utils/profilePhoto";
import type { User } from "../../types/Types";
import UploadImage from "../Account/UploadImage";

/** Session-only: cleared on logout so the prompt shows again on the next login. */
const DISMISS_KEY_PREFIX = "explorerPhotoPromptDismissed:";

function dismissStorageKey(objectId: string): string {
  return `${DISMISS_KEY_PREFIX}${objectId}`;
}

function wasPromptDismissed(objectId: string): boolean {
  try {
    return sessionStorage.getItem(dismissStorageKey(objectId)) === "1";
  } catch {
    return false;
  }
}

function markPromptDismissed(objectId: string): void {
  try {
    sessionStorage.setItem(dismissStorageKey(objectId), "1");
    // Drop any legacy permanent dismiss from earlier builds.
    localStorage.removeItem(dismissStorageKey(objectId));
  } catch {
    // ignore quota / private mode
  }
}

function shouldSkipPromptPath(pathname: string): boolean {
  if (pathname === "/") return true;
  if (pathname.startsWith("/video-call")) return true;
  if (pathname.startsWith("/explorer/")) return true;
  if (pathname.includes("signup") || pathname.includes("sign-up")) return true;
  if (pathname.startsWith("/register")) return true;
  return false;
}

/**
 * Optional prompt for explorers without a profile photo — once per login session.
 */
export default function ExplorerProfilePhotoRequiredModal() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { objectId } = useAppContext();
  const [showUploader, setShowUploader] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => !!(objectId && wasPromptDismissed(objectId)),
  );

  const enabled = isLoggedIn() && !!objectId;
  const { isExplorer, profile, isLoading, isReady } = useCurrentMembership();

  const userBag = useMemo(() => (profile || {}) as User, [profile]);

  const alreadyDismissed =
    dismissed || (!!objectId && wasPromptDismissed(objectId));

  const needsPhoto =
    enabled &&
    !isLoading &&
    isReady &&
    isExplorer &&
    !hasProfessionalPhoto(userBag) &&
    !alreadyDismissed &&
    !shouldSkipPromptPath(pathname);

  const dismiss = () => {
    if (objectId) markPromptDismissed(objectId);
    setDismissed(true);
    setShowUploader(false);
  };

  if (!needsPhoto && !showUploader) {
    return null;
  }

  return (
    <>
      <Modal
        show={needsPhoto && !showUploader}
        onHide={dismiss}
        centered
        aria-labelledby="explorer-photo-required-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="explorer-photo-required-title">
            {t("explorer.photoRequired.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">{t("explorer.photoRequired.message")}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="outline-secondary" onClick={dismiss}>
            {t("explorer.photoRequired.dismiss")}
          </Button>
          <Button
            type="button"
            variant="primary"
            className="d-inline-flex align-items-center gap-2"
            onClick={() => setShowUploader(true)}
          >
            <BsCamera aria-hidden />
            {t("explorer.photoRequired.cta")}
          </Button>
        </Modal.Footer>
      </Modal>

      <UploadImage
        showBannerModal={showUploader}
        setShowBannerModal={(open) => {
          setShowUploader(open);
          if (!open) dismiss();
        }}
        userData={userBag}
        variant="profile"
      />
    </>
  );
}
